import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { usePublicConfig } from '@context/PublicConfigContext';
import { reverseGeocodeLatLng, forwardGeocodeQuery } from '@utils/googleGeocode';

const LocationContext = createContext(null);

const DEFAULT_LOCATION = { lat: 28.6139, lng: 77.209 };
const DEFAULT_LOCATION_NAME = 'New Delhi, India';
const CACHE_KEY = 'playfynd_location_cache';
const CACHE_TTL = 30 * 60 * 1000;
const MIN_MOVE_METERS = 200;

const toRad = (deg) => (deg * Math.PI) / 180;

/**
 * Haversine distance in meters between two lat/lng points.
 */
const haversineMeters = (lat1, lng1, lat2, lng2) => {
  const R = 6_371_000;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const readCache = () => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const cached = JSON.parse(raw);
    if (Date.now() - cached.ts > CACHE_TTL) return null;
    return cached;
  } catch {
    return null;
  }
};

const writeCache = (lat, lng, name) => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ lat, lng, name, ts: Date.now() }));
  } catch { /* quota exceeded */ }
};

/**
 * Returns true if the cached location is close enough that we can reuse the name
 * without calling the geocode API again.
 */
const isCacheUsable = (cached, lat, lng) => {
  if (!cached) return false;
  return haversineMeters(cached.lat, cached.lng, lat, lng) < MIN_MOVE_METERS;
};

export const LocationProvider = ({ children }) => {
  const { googleMapsApiKey, ready: publicConfigReady } = usePublicConfig();

  const cached = useRef(readCache()).current;
  const [location, setLocation] = useState(
    cached ? { lat: cached.lat, lng: cached.lng } : DEFAULT_LOCATION
  );
  const [locationName, setLocationName] = useState(cached?.name || 'Detecting...');
  const [deviceLocationName, setDeviceLocationName] = useState(cached?.name || 'Detecting...');
  const [locationError, setLocationError] = useState(null);
  const [locationLoading, setLocationLoading] = useState(!cached);
  const [isManual, setIsManual] = useState(false);
  const apiKeyRef = useRef(googleMapsApiKey);
  const geocodedRef = useRef(false);

  useEffect(() => { apiKeyRef.current = googleMapsApiKey; }, [googleMapsApiKey]);

  const resolvePosition = useCallback(async (position) => {
    const coords = {
      lat: position.coords.latitude,
      lng: position.coords.longitude,
    };

    const existingCache = readCache();
    if (isCacheUsable(existingCache, coords.lat, coords.lng)) {
      setLocation(coords);
      setLocationName(existingCache.name);
      setDeviceLocationName(existingCache.name);
      setLocationLoading(false);
      geocodedRef.current = true;
      return;
    }

    setLocation(coords);
    setLocationLoading(false);

    const key = apiKeyRef.current;
    if (key) {
      const name = await reverseGeocodeLatLng(coords.lat, coords.lng, key);
      if (name) {
        setLocationName(name);
        setDeviceLocationName(name);
        writeCache(coords.lat, coords.lng, name);
        geocodedRef.current = true;
      }
    }
  }, []);

  const handleGeoError = useCallback((error) => {
    setLocationError(error.message);
    if (!readCache()) setLocationName(DEFAULT_LOCATION_NAME);
    setLocationLoading(false);
  }, []);

  useEffect(() => {
    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported');
      if (!cached) setLocationName('Location unavailable');
      setLocationLoading(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      resolvePosition,
      handleGeoError,
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 600000 }
    );
  }, [resolvePosition, handleGeoError, cached]);

  useEffect(() => {
    if (!publicConfigReady || !googleMapsApiKey) return;
    if (isManual || geocodedRef.current) return;
    if (location.lat === DEFAULT_LOCATION.lat && location.lng === DEFAULT_LOCATION.lng) return;

    const existingCache = readCache();
    if (isCacheUsable(existingCache, location.lat, location.lng)) {
      setLocationName(existingCache.name);
      setDeviceLocationName(existingCache.name);
      geocodedRef.current = true;
      return;
    }

    reverseGeocodeLatLng(location.lat, location.lng, googleMapsApiKey).then((name) => {
      if (name) {
        setLocationName(name);
        setDeviceLocationName(name);
        writeCache(location.lat, location.lng, name);
        geocodedRef.current = true;
      }
    });
  }, [publicConfigReady, googleMapsApiKey, isManual, location.lat, location.lng]);

  const boundForwardGeocode = useCallback(
    (query) => forwardGeocodeQuery(query, apiKeyRef.current),
    []
  );

  const setManualLocation = useCallback(async (lat, lng, name) => {
    setLocation({ lat, lng });
    setLocationName(name || 'Custom location');
    setIsManual(true);
    writeCache(lat, lng, name || 'Custom location');
  }, []);

  const resetToDeviceLocation = useCallback(() => {
    setIsManual(false);
    geocodedRef.current = false;
    setLocationLoading(true);
    setLocationName('Detecting...');

    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported');
      setLocationName('Location unavailable');
      setLocationLoading(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      resolvePosition,
      handleGeoError,
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 600000 }
    );
  }, [resolvePosition, handleGeoError]);

  return (
    <LocationContext.Provider value={{
      location,
      locationName,
      deviceLocationName,
      locationError,
      locationLoading,
      isManual,
      setLocation,
      setManualLocation,
      resetToDeviceLocation,
      forwardGeocode: boundForwardGeocode,
    }}>
      {children}
    </LocationContext.Provider>
  );
};

export const useLocation = () => {
  const context = useContext(LocationContext);
  if (!context) throw new Error('useLocation must be used within LocationProvider');
  return context;
};
